import { Link } from 'react-router-dom';
import { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Facebook, Twitter, Instagram, Youtube, ExternalLink, Bell, Loader2, Check } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import api from '@/utils/api';
import { toast } from 'sonner';

const MIREX_LOGO = 'https://customer-assets.emergentagent.com/job_56b9fc4d-0615-46a9-bb44-4b743949a554/artifacts/iv1nhi8r_Logo_Mirex_color_2.png';

export default function Footer() {
  const { t } = useLanguage();
  const year = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post('/newsletter/subscribe', { email });
      setSubscribed(true);
      toast.success('Inscrição realizada com sucesso!');
      setEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao subscrever');
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#0A0A0A] text-gray-300 border-t-4 border-[#FFD700]">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand - Updated with MIREX logo */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={MIREX_LOGO} alt="MIREX" className="h-12 w-auto" />
            </div>
            <p className="text-sm font-semibold text-white mb-2">Embaixada de Angola no Reino de Espanha</p>
            <p className="text-sm text-gray-400 leading-relaxed mb-4">
              Ao serviço dos cidadãos angolanos em Espanha, promovendo a cooperação bilateral entre Angola e Espanha.
            </p>
            <div className="flex gap-3">
              {[
                { icon: Facebook, href: '#', label: 'Facebook' },
                { icon: Twitter, href: '#', label: 'Twitter/X' },
                { icon: Instagram, href: '#', label: 'Instagram' },
                { icon: Youtube, href: '#', label: 'YouTube' },
              ].map(({ icon: Icon, href, label }) => (
                <a key={label} href={href} aria-label={label}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-800 hover:bg-[#CC0000] transition-colors">
                  <Icon size={14} />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Serviços Consulares</h3>
            <ul className="space-y-2 text-sm">
              {[
                { label: 'Bilhete de Identidade', path: '/servicos-consulares' },
                { label: 'Cartão Consular', path: '/cartao-consular' },
                { label: 'Certidões de Registo Civil', path: '/servicos-consulares' },
                { label: 'Passaporte', path: '/servicos-consulares' },
                { label: 'Registo Criminal', path: '/servicos-consulares' },
                { label: 'Salvo-Conduto', path: '/salvo-conduto' },
                { label: 'Serviços Notários', path: '/servicos-consulares' },
                { label: 'Vistos', path: '/servicos-consulares' },
              ].map(({ label, path }) => (
                <li key={label}>
                  <Link to={path} className="text-gray-400 hover:text-[#FFD700] transition-colors">{label}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Link's Úteis</h3>
            <ul className="space-y-2 text-sm">
              {[
                { label: 'APIEX', href: 'https://www.aipex.gov.ao/' },
                { label: 'MINEA', href: 'https://www.minea.gov.ao/' },
                { label: 'MINDCOM', href: 'https://www.mindcom.gov.ao/' },
                { label: 'MIREMPET', href: 'https://mirempet.gov.ao/' },
                { label: 'MINAGRIF', href: 'https://www.minagrif.gov.ao/' },
              ].map(({ label, href }) => (
                <li key={label}>
                  <a href={href} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 text-gray-400 hover:text-[#FFD700] transition-colors">
                    {label} <ExternalLink size={11} />
                  </a>
                </li>
              ))}
              <li>
                <a href="https://mirex.gov.ao" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 text-gray-400 hover:text-[#FFD700] transition-colors">
                  MIREX <ExternalLink size={11} />
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Contactos</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-2">
                <MapPin size={14} className="text-[#FFD700] flex-shrink-0 mt-0.5" />
                <span className="text-gray-400">Calle Lagasca, 88, 2º Andar<br />Salamanca, 28001 Madrid, España</span>
              </li>
              <li className="flex gap-2 items-center">
                <Phone size={14} className="text-[#FFD700] flex-shrink-0" />
                <a href="tel:+34914356430" className="text-gray-400 hover:text-white transition-colors">+34 914 35 64 30</a>
              </li>
              <li className="flex gap-2 items-center">
                <Mail size={14} className="text-[#FFD700] flex-shrink-0" />
                <a href="mailto:embaixada.espanha@mirex.gov.ao" className="text-gray-400 hover:text-white transition-colors text-xs">
                  embaixada.espanha@mirex.gov.ao
                </a>
              </li>
              <li className="flex gap-2">
                <Clock size={14} className="text-[#FFD700] flex-shrink-0 mt-0.5" />
                <div className="text-gray-400">
                  <p>Seg–Sex: 09h–15h</p>
                  <p className="text-[#CC0000] font-medium text-xs mt-1">Emergência 24h: +34 914 35 64 30</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Newsletter */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Bell size={20} className="text-[#FFD700]" />
              <div>
                <p className="text-white font-semibold text-sm">Subscreva a Newsletter</p>
                <p className="text-xs text-gray-400">Receba as últimas notícias e comunicados</p>
              </div>
            </div>
            {subscribed ? (
              <div className="flex items-center gap-2 text-green-400 text-sm">
                <Check size={16} />
                <span>Subscrito com sucesso!</span>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} className="flex gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-red-500/20 focus:border-red-500 outline-none w-64"
                  data-testid="newsletter-email-footer"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="px-5 py-2 bg-[#CC0000] text-white text-sm font-medium rounded-lg hover:bg-[#A30000] disabled:opacity-50 transition-colors flex items-center gap-2"
                  data-testid="newsletter-submit-footer"
                >
                  {loading ? <Loader2 size={14} className="animate-spin" /> : 'Subscrever'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-gray-500">
          <p>© {year} Embaixada da República de Angola em Espanha. Todos os direitos reservados.</p>
          <div className="flex gap-4">
            <Link to="/sobre" className="hover:text-gray-300 transition-colors">Política de Privacidade</Link>
            <Link to="/sobre" className="hover:text-gray-300 transition-colors">Termos de Uso</Link>
            <Link to="/contactos" className="hover:text-gray-300 transition-colors">Contactos</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
